
import { LeadContact } from "@/services/leadContactsService";
import { Button } from "@/components/ui/button";
import { FileText, Plus } from "lucide-react";
import ContactCard from "./ContactCard";
import { Skeleton } from "@/components/ui/skeleton";

interface ContactsListProps {
  contacts: LeadContact[];
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  departmentFilter: string;
  onAddContact?: () => void;
  onEditContact?: (contact: LeadContact) => void;
  onViewContact?: (contact: LeadContact) => void;
  onDeleteContact?: (contact: LeadContact) => void;
  onInitiateCall?: (contact: LeadContact) => void;
}

const ContactsList = ({ 
  contacts, 
  isLoading, 
  error,
  searchQuery,
  departmentFilter,
  onAddContact,
  onEditContact,
  onViewContact,
  onDeleteContact,
  onInitiateCall
}: ContactsListProps) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {[...Array(6)].map((_, index) => (
          <div key={index} className="border rounded-lg p-4">
            <div className="flex items-center gap-4">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-28" />
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-3/4" />
              <div className="pt-2 flex gap-2">
                <Skeleton className="h-8 w-8 rounded-full" />
                <Skeleton className="h-8 w-8 rounded-full" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4 text-center">
        <h3 className="font-semibold text-destructive mb-2">Error Loading Contacts</h3>
        <p className="text-sm">{error}</p>
        <Button 
          variant="outline" 
          className="mt-4"
          onClick={() => window.location.reload()}
        >
          Try Again
        </Button>
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="text-center py-16 border-2 border-dashed rounded-lg">
        <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <FileText className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium mb-2">No contacts found</h3>
        <p className="text-muted-foreground mb-4">
          {searchQuery || departmentFilter !== "All" ? 
            "Try adjusting your search filters to find what you're looking for." :
            "Get started by adding your first contact."}
        </p>
        {onAddContact && (
          <Button className="flex items-center mx-auto" onClick={onAddContact}>
            <Plus size={16} className="mr-2" />
            Add Your First Contact
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {contacts.map((contact) => (
        <ContactCard 
          key={contact.id} 
          contact={contact} 
          onEdit={onEditContact}
          onView={onViewContact}
          onDelete={onDeleteContact}
          onInitiateCall={onInitiateCall}
        />
      ))}
    </div>
  );
};

export default ContactsList;
